import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Reply } from 'lucide-react';
import type { MailRow } from '@/lib/crm-client';
import { ReplySentimentButtons } from '@/components/ReplySentimentButtons';
import { ListenKarte } from './ListenKarte';
import { ZEILEN } from './useDashboardData';

/**
 * Die Antworten auf Erstansprachen, die noch auf ihre Einordnung warten.
 *
 * Hier steht, was früher unter „Antworten“ im Menü lag: wer auf eine
 * Kampagnenmail geantwortet hat und ob das ein Ja oder ein Nein war. Positiv /
 * Abgesagt lässt sich gleich in der Zeile drücken — dieselbe Bewertung wie im
 * Posteingang, an derselben Nachricht, mit denselben Folgen (die Antwort zählt
 * in der Kampagne, „Positiv“ legt den Deal an).
 *
 * Bewertete Antworten fallen aus der Liste: sie sind erledigt, und eine Liste
 * von Arbeit soll leer werden dürfen. Eine Bewertung zurücknehmen, die ganze
 * Unterhaltung lesen und antworten geht im Posteingang — eine Antwort ist
 * Post, und der Klick auf die Zeile führt dorthin.
 *
 * „Alle Antworten“ führt auf `/leads/antworten`: die Seite bietet, was hier
 * nicht steht (Deal anlegen, als Kontakt übernehmen, eine Antwort ohne
 * Bewertung zählen und die Sequenz beenden) und ist sonst nirgends verlinkt.
 */
export function AntwortenCard({
  antworten,
  onChanged,
}: {
  antworten: MailRow[];
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [fehler, setFehler] = useState<string | null>(null);

  return (
    <ListenKarte
      titel="Antworten"
      icon={Reply}
      mehr={{ label: 'Alle Antworten', onClick: () => navigate('/leads/antworten') }}
      daneben={{ label: 'Posteingang', onClick: () => navigate('/posteingang') }}
      leer="Keine Antwort wartet auf eine Einordnung."
      gesamt={antworten.length}
      gezeigt={Math.min(antworten.length, ZEILEN)}
    >
      {antworten.slice(0, ZEILEN).map((m) => (
        <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
          <button
            onClick={() => navigate('/posteingang')}
            className="min-w-0 flex-1 basis-48 text-left transition-colors hover:text-foreground"
          >
            <p className="truncate text-sm font-medium">
              {m.contactName ?? m.fromAddress ?? 'Unbekannt'}
              {m.organizationName && (
                <span className="font-normal text-muted-foreground">
                  {' · '}
                  {m.organizationName}
                </span>
              )}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {m.subject || '(ohne Betreff)'}
              {m.campaignName && ` · ${m.campaignName}`}
              {m.receivedAt && ` · ${new Date(m.receivedAt).toLocaleDateString('de-DE')}`}
            </p>
          </button>
          <div className="flex shrink-0 gap-1">
            <ReplySentimentButtons
              messageId={m.id}
              value={m.replySentiment}
              onChanged={() => {
                setFehler(null);
                onChanged();
              }}
              onError={setFehler}
            />
          </div>
        </li>
      ))}
      {fehler && <li className="px-4 py-2 text-xs text-destructive">{fehler}</li>}
    </ListenKarte>
  );
}
